import { Check, ChevronDown } from 'lucide-react';
import { useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useClickOutside, useEscape } from '../../lib/hooks';
import { cx } from './cx';
import styles from './Menu.module.css';

export interface PopoverProps {
  open: boolean;
  onClose: () => void;
  trigger: ReactNode;
  children: ReactNode;
  align?: 'left' | 'right' | 'stretch';
  /** Opens upwards, e.g. for the account menu at the bottom of the sidebar. */
  placement?: 'bottom' | 'top';
  width?: number;
  className?: string;
}

/** Anchored panel closed by outside clicks and Escape. */
export function Popover({ open, onClose, trigger, children, align = 'left', placement = 'bottom', width, className }: PopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, onClose, open);
  useEscape(onClose, open);
  const style: CSSProperties = width ? { width } : {};
  return (
    <div ref={ref} className={cx(styles.anchor, className)}>
      {trigger}
      {open ? (
        <div className={cx(styles.panel, styles[align], styles[placement])} style={style} role="menu">
          {children}
        </div>
      ) : null}
    </div>
  );
}

export interface MenuItemProps {
  onSelect: () => void;
  children: ReactNode;
  selected?: boolean;
  icon?: ReactNode;
  danger?: boolean;
}

export function MenuItem({ onSelect, children, selected, icon, danger }: MenuItemProps) {
  return (
    <button type="button" role="menuitem" className={cx(styles.item, danger ? styles.danger : undefined)} onClick={onSelect}>
      {icon ? <span className={styles.itemIcon}>{icon}</span> : null}
      <span className={styles.itemLabel}>{children}</span>
      {selected ? <Check size={14} strokeWidth={2} aria-hidden /> : null}
    </button>
  );
}

export function MenuDivider() {
  return <div className={styles.divider} />;
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  /** «field» matches inputs (46px, radius 12); «pill» is the compact filter style. */
  variant?: 'field' | 'pill';
  invalid?: boolean;
  id?: string;
  className?: string;
  menuWidth?: number;
  icon?: ReactNode;
  /** What is being chosen, for screen readers when no visible label points at it: «Курс: Все курсы». */
  label?: string;
}

export function Select({ value, options, onChange, placeholder = 'Выберите', variant = 'field', invalid, id, className, menuWidth, icon, label }: SelectProps) {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === value);
  return (
    <Popover
      open={open}
      onClose={() => setOpen(false)}
      align={variant === 'field' ? 'stretch' : 'left'}
      width={menuWidth}
      className={className}
      trigger={
        <button
          id={id}
          type="button"
          className={cx(styles.select, styles[variant], invalid ? styles.invalid : undefined)}
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={label ? `${label}: ${current?.label ?? placeholder}` : undefined}
        >
          {icon}
          <span className={cx(styles.selectLabel, current ? undefined : styles.placeholder)}>{current?.label ?? placeholder}</span>
          <ChevronDown size={variant === 'field' ? 16 : 14} strokeWidth={1.6} className={styles.chevron} aria-hidden />
        </button>
      }
    >
      {options.length === 0 ? <div className={styles.empty}>Нет вариантов</div> : null}
      {options.map((option) => (
        <MenuItem
          key={option.value}
          selected={option.value === value}
          onSelect={() => {
            onChange(option.value);
            setOpen(false);
          }}
        >
          {option.label}
        </MenuItem>
      ))}
    </Popover>
  );
}
